import { Schedule } from "src/components/schedule/types";
import { DestinationInstancesSetInput } from "src/graphql";
import { OverrideConfig } from "src/components/destinations/types";
import { isFieldEmpty } from "src/components/destinations/utils";
import merge from "lodash/merge";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import { unflatten } from "@hightouch/lib/util/flatten";

export type SyncWithTemplateInformation = {
  id: string;
  destination: {
    // actually a number, bigint problem
    id: string;
  } | null;
  config: Record<string, unknown>;
  is_streaming: boolean | null;
  schedule: Schedule | null;
  schedule_paused: boolean | null;
  row_threshold_total: string | null;
  row_threshold_attempted: string | null;
  // Effective Match Booster region while attached (from the syncs view /
  // template). Copied onto the ordinary sync-config column on detach.
  matchbooster_region: string | null;
};

// Template configs can contain either literal dotted keys ("add.body")
// or nested objects ({ add: { body } }), because template overrides use
// flat dotted keys. Check literal keys first so dotted keys are read
// correctly, then fall back to nested-path lookup.
export const getTemplateConfigValueByNodeKey = (
  templateConfig: Record<string, unknown> | null | undefined,
  nodeKey: string,
): unknown => {
  if (!templateConfig) {
    return undefined;
  }

  if (Object.hasOwn(templateConfig, nodeKey)) {
    return templateConfig[nodeKey];
  }

  return get(templateConfig, nodeKey);
};

const getEmptyFieldPaths = (
  config: Record<string, unknown>,
  value: unknown,
  path: string,
): string[] => {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const childPaths = Object.entries(value).flatMap(([childKey, childValue]) =>
      getEmptyFieldPaths(
        config,
        childValue,
        path ? `${path}.${childKey}` : childKey,
      ),
    );

    if (!path) {
      return childPaths;
    }

    return childPaths.length > 0 || isFieldEmpty(path, config)
      ? [path, ...childPaths]
      : [];
  }

  return path && isFieldEmpty(path, config) ? [path] : [];
};

export const getDetachedSyncInputFromSync = (
  sync: SyncWithTemplateInformation,
): DestinationInstancesSetInput => {
  return {
    destination_id: sync.destination?.id,
    sync_template_id: null,
    // The sync config is the transformed config
    // The sync template overrides are merged in the transformed config
    config: sync.config,
    is_streaming: sync.is_streaming,
    schedule: sync.schedule,
    schedule_paused: sync.schedule_paused,
    row_threshold_total: sync.row_threshold_total,
    row_threshold_attempted: sync.row_threshold_attempted,
    // Clear the sync template overrides column
    sync_template_overrides: null,
    // Pin the inherited template region onto the ordinary sync-config column
    // before clearing sync_template_id.
    matchbooster_region: sync.matchbooster_region ?? null,
    // we null the draft id, it gets added on the backend and we want to be consistent
    // if a workspace turns off approvals again
    approved_draft_id: null,
  };
};

/**
 * Helper function to build an override config off the template
 * It includes fields that are 1. marked as overridable on the template
 * and 2. empty on the template's config. This is to allow the downstream
 * syncs to set values for those fields.
 */
export const getOverrideConfigIncludingEmptyFields = (
  syncTemplate?: {
    id?: number | null;
    override_config?: OverrideConfig | any;
    config?: Record<string, unknown> | any;
  } | null,
): OverrideConfig | null => {
  if (!syncTemplate) return null;

  const baseOverrideConfig = syncTemplate.override_config || {};
  const overridableFields: OverrideConfig = {};
  if (syncTemplate.config) {
    getEmptyFieldPaths(syncTemplate.config, syncTemplate.config, "").forEach(
      (key) => {
        // If a field is empty on the template, we mark it as overridable,
        // so it can be displayed in the UI. The override config takes precedence
        // over emptiness/nullability
        overridableFields[key] = { overridable: true };
      },
    );
  }
  // Merge with existing override config, but only keep fields that are:
  // 1. Already marked as overridable (or not) in the template, OR
  // 2. Empty in the template config
  const finalOverrideConfig: OverrideConfig = {};
  Object.entries(baseOverrideConfig).forEach(([key, config]) => {
    if (config && typeof config === "object") {
      finalOverrideConfig[key] = {
        overridable: "overridable" in config && Boolean(config.overridable),
      };
    }
  });
  // Add empty fields, but don't override existing explicit settings
  Object.entries(overridableFields).forEach(([key, value]) => {
    if (!finalOverrideConfig[key]) {
      finalOverrideConfig[key] = value;
    }
  });
  return finalOverrideConfig;
};

/**
 * Merge override updates from a sync form submission into the sync's stored
 * overrides. The form seeds every stored override value on load, so a key
 * that comes back explicitly `undefined` was cleared during this session
 * (e.g. by a formkit reset on a mode switch) — remove its override so it
 * can't go stale. Keys absent from `updates` keep their stored value.
 */
export const applySyncTemplateOverrideUpdates = (
  existingOverrides: Record<string, unknown> | null | undefined,
  updates: Record<string, unknown>,
): Record<string, unknown> => {
  const merged = { ...(existingOverrides ?? {}) };
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) {
      delete merged[key];
    } else {
      merged[key] = value;
    }
  }
  return merged;
};

/**
 * Helper function to merge the sync.config, sync.sync_template_overrides, and
 * sync_template.config objects into a single config that we can use for validation.
 *
 * Note that template configs uses nested objects ({ add: { method: "GET" } }) but
 * syncTemplateOverrides uses flat dotted keys ({ "add.method": "POST" }).
 * lodash.merge can't reconcile these — it treats "add.method" as a literal
 * key, producing both representations in the output.
 * Shallow-spread + unflatten resolves this by normalizing everything into
 * the nested structure.
 *
 * Note, if we're using a syncTemplate, the final sync.config should be empty, but
 * it may have values in the current session if the user is editing the form.
 */
export const mergeSyncConfigWithTemplateAndOverrides = (opts: {
  syncConfig: Record<string, unknown> | null;
  syncTemplate?: {
    config?: Record<string, unknown>;
  };
  syncTemplateOverrides?: Record<string, unknown>;
  unflattenDotNotationKeys: boolean;
}): Record<string, unknown> | null => {
  const {
    syncTemplate,
    syncConfig,
    syncTemplateOverrides,
    unflattenDotNotationKeys,
  } = opts;
  // If no template or template has no config, return the syncConfig
  if (!syncTemplate?.config) {
    return syncConfig;
  }

  if (unflattenDotNotationKeys) {
    // Mirror the backend strategy (migrator.ts): shallow-assign flat override
    // keys onto the template config, then unflatten to resolve dot-notation
    // paths. We use shallow spreads instead of lodash.merge to avoid creating
    // both flat keys (e.g., "add.body") and nested paths ({ add: { body } }),
    const withOverrides = {
      ...syncTemplate.config,
      ...syncTemplateOverrides,
      ...syncConfig,
    };
    return unflatten(withOverrides, { delimiter: ".", overwrite: true });
  }

  // Start with the template's config
  const mergedConfig = cloneDeep(syncTemplate.config);
  merge(mergedConfig, syncConfig);
  // Apply sync template overrides
  if (syncTemplateOverrides) {
    merge(mergedConfig, { ...syncTemplateOverrides });
  }

  return mergedConfig;
};
